// https://elazizi.com/posts/react-query-auth-token-refresh/#simple-project-setup
import type { AxiosError } from 'axios';
import { useMutation, useQuery } from '@tanstack/react-query';

import { api } from '../client';
import {
  CommonStatusRes,
  GetSystemNotificationRes,
  PatchSystemNotificationUid,
  PostSystemNotificationReq,
} from '../types/';
import React from 'react';
import { UserContext } from '../context/userContext';
import { enqueueSnackbar } from 'notistack';

const getSystemNotification = () => {
  return api.get<AxiosError, GetSystemNotificationRes>(
    '/v1/system-notification',
  );
};

const postSystemNotification = (reqData: PostSystemNotificationReq) => {
  return api.post<AxiosError, CommonStatusRes>(
    `/v1/system-notification`,
    reqData,
  );
};

const patchSystemNotificationUid = ({ uid }: PatchSystemNotificationUid) => {
  return api.patch<AxiosError, CommonStatusRes>(
    `/v1/system-notification/${uid}`,
  );
};

export const notificationService = () => {
  const { loggedIn } = React.useContext(UserContext);

  const systemNotificationList = useQuery({
    queryKey: ['getSystemNotification'],
    queryFn: getSystemNotification,
    enabled: loggedIn,
  });

  const sendSystemNotificationMutation = useMutation({
    mutationFn: postSystemNotification,
    onError: ({ message }) => {
      enqueueSnackbar(`postSystemNotification error:  ${message}`, {
        variant: 'error',
      });
    },
    onSuccess: () => {
      enqueueSnackbar('Successfully updated notification.', {
        variant: 'success',
      });
    },
  });

  const clearSystemNotificationMutation = useMutation({
    mutationFn: patchSystemNotificationUid,
    onError: ({ message }) => {
      enqueueSnackbar(`patchSystemNotificationUid error:  ${message}`, {
        variant: 'error',
      });
    },
    onSuccess: () => {
      enqueueSnackbar('Successfully cleared notification.', {
        variant: 'success',
      });
    },
  });

  return {
    systemNotificationList: systemNotificationList.data?.data.notifications,
    sendSystemNotification: sendSystemNotificationMutation.mutate,
    clearSystemNotification: clearSystemNotificationMutation.mutate,
  };
};
